/**
 * /app/$tenantSlug/settings/attendance — Settings → Time & Leave →
 * Attendance & Geofence (HR-admin).
 *
 * Punching (Off / Optional / Required, "Missed punch cuts pay" under
 * Required, the "Doesn't need to punch" list) and Work from home (migration
 * 167 — components/attendance/punching-settings), then office geofence
 * anchors (CRUD) + the tenant attendance toggles: selfie required (off by
 * default), geofence strict, and the default weekly-off day. All server-validated; the page is HR-admin
 * (the backend re-checks every write).
 *
 * QA 2026-10 leave #19: every toggle confirms what it changed (it used to
 * save silently), the payroll-LOP switch says what it does, and the weekly
 * off says it also decides which days a leave request charges — it is the
 * one tenant setting Settings → Leave shows too.
 */

import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Loader2 } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toApiError } from "@/lib/api";
import {
  useLocations,
  useCreateLocation,
  useDeleteLocation,
  useAttendanceSettings,
  useUpdateAttendanceSettings,
} from "@/hooks/use-attendance";
import {
  useTimesheetSettings,
  useUpdateTimesheetSettings,
} from "@/hooks/use-timesheet";
import { minutesToHoursString } from "@/lib/timesheet-format";
import { AttendanceRecomputeDialog } from "@/components/attendance/attendance-recompute-dialog";
import { PunchingSettingsCards } from "@/components/attendance/punching-settings";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/attendance",
)({
  component: SettingsAttendanceRoute,
});

/**
 * Weekly-off-day bits. The mask is 0–127 with Mon=bit0(1) .. Sun=bit6(64),
 * matching the backend (internal/services/attendance/derive.go weekdayBit()):
 * Mon=1, Tue=2, Wed=4, Thu=8, Fri=16, Sat=32, Sun=64. Listed Mon→Sun so the
 * picker reads as a standard work-week.
 */
const WEEKDAYS = [
  { label: "Mon", bit: 1 },
  { label: "Tue", bit: 2 },
  { label: "Wed", bit: 4 },
  { label: "Thu", bit: 8 },
  { label: "Fri", bit: 16 },
  { label: "Sat", bit: 32 },
  { label: "Sun", bit: 64 },
] as const;

function SettingsAttendanceRoute() {
  const settings = useAttendanceSettings();
  const updateSettings = useUpdateAttendanceSettings();
  const locations = useLocations();
  const createLoc = useCreateLocation();
  const deleteLoc = useDeleteLocation();

  // 085 / docs/47 — tenant timesheet settings (loose/strict + standard day).
  const tsSettings = useTimesheetSettings();
  const updateTsSettings = useUpdateTimesheetSettings();

  const [name, setName] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("200");

  // Standard work hours input — edited as an hours string, persisted as
  // minutes. Seeded from the resolved server value; re-seeded when the
  // server value lands.
  const [stdHours, setStdHours] = useState("");
  const serverStdMinutes = tsSettings.data?.standard_work_minutes;
  useEffect(() => {
    if (serverStdMinutes != null) {
      setStdHours(minutesToHoursString(serverStdMinutes));
    }
  }, [serverStdMinutes]);

  async function addLocation() {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!name.trim() || Number.isNaN(latitude) || Number.isNaN(longitude)) {
      toast.error("Name, latitude and longitude are required");
      return;
    }
    try {
      await createLoc.mutateAsync({
        name: name.trim(),
        latitude,
        longitude,
        radius_metres: Number(radius) || 200,
      });
      toast.success("Office location added");
      setName("");
      setLat("");
      setLng("");
      setRadius("200");
    } catch {
      toast.error("Failed to add location");
    }
  }

  // B4 (docs/51): flip a single weekly-off bit in the 0–127 mask and persist
  // the whole mask. Reads the current mask off the server snapshot so toggles
  // compose; the backend re-validates the 0–127 range.
  async function toggleWeeklyOff(bit: number, on: boolean) {
    const current = settings.data?.default_weekly_off_mask ?? 0;
    const next = on ? current | bit : current & ~bit;
    if (next === current) return;
    try {
      await updateSettings.mutateAsync({ default_weekly_off_mask: next });
      const days = WEEKDAYS.filter((d) => (next & d.bit) !== 0).map((d) => d.label);
      toast.success(
        days.length > 0
          ? `Weekly off saved: ${days.join(", ")}.`
          : "Weekly off saved: none — every day is a working day.",
      );
    } catch (err) {
      toast.error(toApiError(err).message ?? "Failed to update weekly off days");
    }
  }

  async function toggleRequireFullAllocation(value: boolean) {
    try {
      await updateTsSettings.mutateAsync({ require_full_allocation: value });
      toast.success("Saved");
    } catch {
      toast.error("Failed to update setting");
    }
  }

  async function saveStandardHours() {
    const hours = Number(stdHours);
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
      toast.error("Standard work hours must be between 0 and 24");
      return;
    }
    // Store as integer minutes (the wire field). Round to the nearest
    // minute so a fractional hour (e.g. 8.5) maps cleanly.
    const minutes = Math.round(hours * 60);
    try {
      await updateTsSettings.mutateAsync({ standard_work_minutes: minutes });
      toast.success("Saved");
    } catch {
      toast.error("Failed to update standard work hours");
    }
  }

  const s = settings.data;
  const ts = tsSettings.data;

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Attendance &amp; Geofence</h2>
            <p className="text-muted-foreground text-sm">
              Choose whether people punch, configure punch capture and office
              geofence anchors.
            </p>
          </div>

          <PunchingSettingsCards settings={s} loading={settings.isLoading} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Capture Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/*
                B4 (docs/51): selfie-required and strict-geofence are not
                enforced in v1 — the punch path neither captures a selfie nor
                hard-rejects out-of-fence punches. Presenting them as live
                toggles lied. They are disabled + labelled "Not enforced yet"
                until the enforcement lands; the persisted setting is kept so
                nothing is lost on the backend.
              */}
              <label className="flex items-start gap-2 text-sm text-muted-foreground pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:py-3.5">
                <Checkbox
                  className="mt-0.5"
                  checked={s?.selfie_required ?? false}
                  disabled
                  aria-disabled
                />
                <span>
                  <span className="block">Require a selfie at punch time</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Coming soon — not enforced yet (v1). Punches are accepted
                    without a selfie regardless of this setting.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-muted-foreground pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:py-3.5">
                <Checkbox
                  className="mt-0.5"
                  checked={s?.geofence_strict ?? false}
                  disabled
                  aria-disabled
                />
                <span>
                  <span className="block">Strict geofence</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Coming soon — not enforced yet (v1). Out-of-fence punches
                    are accepted and flagged, never blocked.
                  </span>
                </span>
              </label>
              {/* "Missed punch cuts pay" (drives_payroll_lop) lives in the
                  Punching card above, shown only under Required. */}
            </CardContent>
          </Card>

          {/*
            B4 (docs/51): weekly-off-day picker. Without this, weekends had no
            way to be marked off, so they derived as red "absent". The mask is
            a 0–127 bitmask, Mon=bit0(1) .. Sun=bit6(64) — verified against
            backend internal/services/attendance/derive.go weekdayBit().
            Persisted via the same updateSettings mutation (carries
            default_weekly_off_mask). Default seed is 64 (Sunday).
          */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Weekly Off Days</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Days selected here are treated as weekly off: they don&apos;t
                count as absent when no attendance is recorded, and a leave
                request never charges them. This is the same setting as the
                weekly off under Settings → Leave. Someone on a rostered
                shift follows that shift&apos;s weekly off instead.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {WEEKDAYS.map((day) => {
                  const mask = s?.default_weekly_off_mask ?? 0;
                  const checked = (mask & day.bit) !== 0;
                  return (
                    <label
                      key={day.bit}
                      className="flex items-center gap-2 text-sm pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:min-h-11"
                    >
                      <Checkbox
                        checked={checked}
                        disabled={
                          settings.isLoading || updateSettings.isPending
                        }
                        onCheckedChange={(c) =>
                          toggleWeeklyOff(day.bit, c === true)
                        }
                      />
                      {day.label}
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/*
            B-A19 #1: surface the recompute batch op. After changing
            weekly-off, geofence or shift settings the existing day rows are
            stale until re-derived; this re-runs POST /attendance/recompute
            over a range. Maintenance op (not destructive) → a Dialog.
          */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recompute Attendance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Re-derive attendance days after changing weekly-off, geofence
                or shift settings.
              </p>
              <AttendanceRecomputeDialog />
            </CardContent>
          </Card>

          {/* 085 / docs/47 — Time (timesheet allocation) settings. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Time</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-2 text-sm pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:py-3.5">
                <Checkbox
                  className="mt-0.5"
                  checked={ts?.require_full_allocation ?? false}
                  disabled={tsSettings.isLoading || updateTsSettings.isPending}
                  onCheckedChange={(c) =>
                    toggleRequireFullAllocation(c === true)
                  }
                />
                <span>
                  <span className="block">Require full-day allocation</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Loose (default): any unlogged time auto-fills into General
                    on submit. Strict: also rejects submitting a day whose
                    logged hours exceed its target.
                  </span>
                </span>
              </label>

              <div className="max-w-xs">
                <Label htmlFor="std-hours">Standard Work Hours</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    id="std-hours"
                    type="text"
                    inputMode="decimal"
                    className="h-9 w-24 text-center tabular-nums"
                    value={stdHours}
                    disabled={tsSettings.isLoading}
                    onChange={(e) => setStdHours(e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">
                    hours / day
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={saveStandardHours}
                    disabled={tsSettings.isLoading || updateTsSettings.isPending}
                  >
                    Save
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The daily fill target used when no attendance is recorded
                  for a day (default 9h).
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Office Locations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid items-end gap-3 sm:grid-cols-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="ln">Name</Label>
                  <Input id="ln" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lat">Latitude</Label>
                  <Input id="lat" value={lat} onChange={(e) => setLat(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lng">Longitude</Label>
                  <Input id="lng" value={lng} onChange={(e) => setLng(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="rad">Radius (m)</Label>
                  <Input id="rad" value={radius} onChange={(e) => setRadius(e.target.value)} />
                </div>
              </div>
              <Button onClick={addLocation} disabled={createLoc.isPending}>
                Add Location
              </Button>

              <ul className="divide-y text-sm">
                {(locations.data?.locations ?? []).map((l) => (
                  <li key={l.id} className="flex items-center justify-between py-2">
                    <span>
                      {l.name} — {l.latitude}, {l.longitude} ({l.radius_metres} m)
                      {!l.is_active && " (inactive)"}
                    </span>
                    <RemoveLocationButton
                      name={l.name}
                      onConfirm={() => deleteLoc.mutateAsync(l.id)}
                      pending={deleteLoc.isPending}
                    />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </SettingsShell>
    </>
  );
}

/**
 * Per-row confirm gate for removing an office geofence location.
 * Deleting an anchor means punches are no longer checked against this
 * geofence, so the one-click Remove is wrapped in an AlertDialog
 * (SOP §3 Rule 5 — destructive actions confirm first).
 */
function RemoveLocationButton({
  name,
  onConfirm,
  pending,
}: {
  readonly name: string;
  readonly onConfirm: () => Promise<unknown>;
  readonly pending: boolean;
}) {
  const [open, setOpen] = useState(false);

  async function handleConfirm() {
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      // The mutation surfaces its own error handling; keep the dialog open.
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="ghost" disabled={pending}>
          Remove
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Remove &ldquo;{name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This deletes the office location; punches will no longer be
            checked against this geofence.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            // Keep the dialog open while the mutation runs; handleConfirm
            // closes it on success. Default Action auto-closes on click.
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={pending}
            className={cn(
              buttonVariants({ variant: "destructive" }),
              "gap-1.5",
            )}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
